// Gewinnspiel: anlegen, starten, ziehen, auswerten.
//
// Die Seite macht zwei Dinge, die es sonst nirgends gibt: Sie zeigt echte
// Kontaktdaten von Hörern, und sie zieht den Gewinner. Beides darf nur, wer auch
// senden darf – Moderation und Leitung. Alle anderen sehen die Zahlen, aber
// keinen Namen.
//
// Gezogen wird zufällig; jede Ziehung hält fest, wann sie war und wie viele Lose
// in der Trommel lagen. Nachziehen geht jederzeit – ein Gerät gewinnt dabei nie
// zweimal.
//
// Sichtbar ist die Seite nur, wenn das Modul „Gewinnspiel“ eingeschaltet ist
// (Aussehen → Mitmach-Module).

import { useCallback, useEffect, useRef, useState } from "react";

import {
  api,
  contestApi,
  type Contest,
  type ContestDetail,
  type ContestEntry,
  type ContestInput,
  type ContestMode,
} from "../api";
import { useAuth } from "../auth/AuthProvider";
import { errorText } from "../components/streaming-ui";
import { Initialen, KanalWahl, Meldungen, ModulAus, StandSchild, Sym } from "../components/social-ui";
import { Badge, Card, EmptyState, Page, PageHeader } from "../components/ui";
import type { Channel } from "../types";

const MODES: Array<{ value: ContestMode; label: string; hint: string }> = [
  { value: "mitmachen", label: "Nur mitmachen", hint: "Name und Kontakt genügen." },
  { value: "frage", label: "Frage beantworten", hint: "Nur richtige Antworten kommen in die Lostrommel." },
  { value: "loesungswort", label: "Lösungswort", hint: "Das Wort, das ihr in der Sendung nennt." },
];

/** Etikettfarbe je Stand – der Text kommt aus STATUS_TEXT. */
const STATUS_STAND: Record<Contest["status"], "entwurf" | "aktiv" | "beendet" | "ausgelost"> = {
  draft: "entwurf",
  open: "aktiv",
  closed: "beendet",
  drawn: "ausgelost",
};

const STATUS_TEXT: Record<Contest["status"], string> = {
  draft: "Entwurf",
  open: "läuft",
  closed: "geschlossen",
  drawn: "ausgelost",
};

const emptyForm = (): ContestInput => ({
  title: "",
  description: "",
  prize: "",
  mode: "mitmachen",
  question: "",
  answer: "",
  contact_kind: "email",
  contact_required: true,
  terms: "",
  starts_at: "",
  ends_at: "",
});

/** Zeitpunkt für <input type="datetime-local"> – ohne Sekunden und ohne Zone. */
function forInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number): string => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Was im Formular steht, in das, was die API erwartet. */
function toIso(value: string | null | undefined): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function whenText(iso: string | null): string {
  if (!iso) return "–";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "–" : d.toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" });
}

function ContestForm({
  busy,
  slug,
  onCreate,
}: {
  busy: boolean;
  slug: string;
  onCreate: (input: ContestInput) => void;
}): JSX.Element {
  const [form, setForm] = useState<ContestInput>(emptyForm());
  const [template, setTemplate] = useState<{ terms: string; keep_days: number } | null>(null);
  const set = (patch: Partial<ContestInput>): void => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    if (!slug) return;
    contestApi.termsTemplate(slug).then(setTemplate).catch(() => setTemplate(null));
  }, [slug]);

  const mode = form.mode ?? "mitmachen";
  const ready = (form.title ?? "").trim().length >= 3 && (mode === "mitmachen" || (form.answer ?? "").trim().length > 0);

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        onCreate({ ...form, starts_at: toIso(form.starts_at), ends_at: toIso(form.ends_at) });
        setForm(emptyForm());
      }}
    >
      <label className="block text-[0.8rem] font-medium text-secondary">
        Titel
        <input
          value={form.title}
          onChange={(e) => set({ title: e.target.value })}
          maxLength={120}
          placeholder="Zwei Karten für das Sommerfest"
          className="input mt-1.5"
        />
      </label>
      <label className="block text-[0.8rem] font-medium text-secondary">
        Beschreibung
        <textarea
          value={form.description}
          onChange={(e) => set({ description: e.target.value })}
          maxLength={2000}
          rows={3}
          className="input mt-1.5"
        />
      </label>
      <label className="block text-[0.8rem] font-medium text-secondary">
        Preis
        <input
          value={form.prize}
          onChange={(e) => set({ prize: e.target.value })}
          maxLength={200}
          placeholder="2 Tageskarten"
          className="input mt-1.5"
        />
      </label>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-[0.8rem] font-medium text-secondary">
          Von (freiwillig)
          <input
            type="datetime-local"
            value={form.starts_at ?? ""}
            onChange={(e) => set({ starts_at: e.target.value })}
            className="input mt-1.5"
          />
        </label>
        <label className="block text-[0.8rem] font-medium text-secondary">
          Einsendeschluss (freiwillig)
          <input
            type="datetime-local"
            value={form.ends_at ?? ""}
            onChange={(e) => set({ ends_at: e.target.value })}
            className="input mt-1.5"
          />
        </label>
      </div>

      <fieldset>
        <legend className="field-label">Art der Teilnahme</legend>
        <div className="grid gap-2 sm:grid-cols-3">
          {MODES.map((m) => (
            <label key={m.value}
              className={`flex cursor-pointer items-start gap-2 rounded-lg border p-3 text-sm transition-colors ${mode === m.value ? "border-brand-primary bg-brand-primary/[0.06]" : "border-default hover:bg-surface-hover"}`}>
              <input type="radio" name="mode" className="mt-0.5" checked={mode === m.value} onChange={() => set({ mode: m.value })} />
              <span>
                <span className="block font-medium text-primary">{m.label}</span>
                <span className="block text-xs text-muted">{m.hint}</span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>
      {mode === "frage" && (
        <label className="block text-[0.8rem] font-medium text-secondary">
          Die Frage
          <input
            value={form.question}
            onChange={(e) => set({ question: e.target.value })}
            maxLength={300}
            className="input mt-1.5"
          />
        </label>
      )}
      {mode !== "mitmachen" && (
        <label className="block text-[0.8rem] font-medium text-secondary">
          {mode === "frage" ? "Die richtige Antwort" : "Das Lösungswort"}
          <input
            value={form.answer}
            onChange={(e) => set({ answer: e.target.value })}
            maxLength={120}
            className="input mt-1.5"
          />
          <span className="field-hint block font-normal">
            Verglichen wird nachsichtig: Groß- und Kleinschreibung, Umlaute und Leerzeichen sind egal.
          </span>
        </label>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-[0.8rem] font-medium text-secondary">
          Kontaktweg für den Gewinner
          <select
            value={form.contact_kind}
            onChange={(e) => set({ contact_kind: e.target.value as ContestInput["contact_kind"] })}
            className="input mt-1.5"
          >
            <option value="email">E-Mail</option>
            <option value="telefon">Telefon</option>
          </select>
        </label>
        <label className="flex h-[2.375rem] cursor-pointer items-center gap-2 self-end text-sm text-primary">
          <input type="checkbox" className="h-4 w-4" checked={form.contact_required !== false} onChange={(e) => set({ contact_required: e.target.checked })} />
          Pflichtfeld
        </label>
      </div>

      <label className="block text-[0.8rem] font-medium text-secondary">
        Teilnahmebedingungen (freiwillig)
        <textarea
          value={form.terms}
          onChange={(e) => set({ terms: e.target.value })}
          maxLength={8000}
          rows={4}
          placeholder="Leer lassen: Dann gilt, was unter Aussehen → Mitmach-Module steht, sonst die Vorlage."
          className="input mt-1.5 font-mono text-xs leading-relaxed"
        />
      </label>
      {template && (
        <details className="well text-sm">
          <summary className="cursor-pointer font-medium text-secondary">Vorlage ansehen</summary>
          <p className="mt-2 text-xs text-muted">
            Das ist eine Hilfe, kein Rechtsrat. Wer sich nicht sicher ist, lässt den Text einmal von jemandem ansehen, der sich damit auskennt.
          </p>
          <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap rounded-md bg-card p-3 text-xs text-secondary">{template.terms}</pre>
          <button
            type="button"
            onClick={() => set({ terms: template.terms })}
            className="btn btn-secondary btn-sm mt-2"
          >
            Vorlage übernehmen und ändern
          </button>
        </details>
      )}

      <div className="flex flex-wrap items-center gap-3 border-t border-default pt-4">
        <button type="submit" disabled={busy || !ready} className="btn btn-primary">
          Anlegen
        </button>
        <p className="text-xs text-muted">Angelegt wird als Entwurf. Erst „Starten“ bringt es auf die Hörerseite.</p>
      </div>
    </form>
  );
}

function EntryList({
  entries,
  keepDays,
  busy,
  onMark,
}: {
  entries: ContestEntry[];
  keepDays: number;
  busy: boolean;
  onMark: (entryId: string, on: boolean) => void;
}): JSX.Element {
  if (entries.length === 0) {
    return <p className="py-2 text-sm text-muted">Noch keine Teilnahmen.</p>;
  }
  return (
    <>
      <p className="text-xs text-muted">
        Name und Kontakt werden nach {keepDays} Tagen automatisch gelöscht. Das Los bleibt, damit die Zahlen stimmen.
      </p>
      <ul className="mt-2 divide-y divide-default overflow-hidden rounded-lg border border-default bg-card text-sm">
        {entries.map((e) => (
          <li key={e.id} className={`flex flex-wrap items-center justify-between gap-2 px-3 py-2.5 ${e.won ? "bg-brand-primary/[0.05]" : ""}`}>
            <span className="flex min-w-0 items-center gap-2.5">
              <Initialen name={e.purged ? "?" : e.name} className="h-7 w-7 text-[0.7rem]" />
              <span className="min-w-0">
                <span className="block truncate text-primary">
                  {e.purged ? <span className="text-muted">(gelöscht)</span> : e.name}
                  {e.contact && <span className="ml-2 text-muted">{e.contact}</span>}
                </span>
                {e.answer && (
                  <span className={`block text-xs ${e.valid ? "text-muted" : "text-red-600 dark:text-red-400"}`}>
                    „{e.answer}“ {e.valid ? "" : "(falsch)"}
                  </span>
                )}
              </span>
            </span>
            <span className="flex items-center gap-3 text-xs text-muted">
              <span className="tabular-nums">{whenText(e.created_at)}</span>
              {e.won && <Badge tone="brand"><Sym name="star" className="h-3 w-3" />Gewinner</Badge>}
              <button
                disabled={busy}
                onClick={() => onMark(e.id, !e.won)}
                className="btn btn-ghost btn-sm"
              >
                {e.won ? "zurücknehmen" : "als Gewinner markieren"}
              </button>
            </span>
          </li>
        ))}
      </ul>
    </>
  );
}

export function GewinnspielPage(): JSX.Element {
  const { user } = useAuth();
  const mayRun = user?.role === "moderator" || user?.role === "tenant_admin" || user?.role === "super_admin";

  const [channels, setChannels] = useState<Channel[]>([]);
  const [slug, setSlug] = useState("");
  const [on, setOn] = useState<boolean | null>(null);
  const [contests, setContests] = useState<Contest[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [detail, setDetail] = useState<ContestDetail | null>(null);
  const [entries, setEntries] = useState<{ keep_days: number; entries: ContestEntry[] } | null>(null);
  const [drawn, setDrawn] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef<number | null>(null);
  // Das Formular für ein neues Gewinnspiel klappt auf Knopfdruck auf; solange es keins gibt, steht es gleich offen.
  const [formOffen, setFormOffen] = useState(false);

  useEffect(() => {
    api.listChannels()
      .then((list) => {
        setChannels(list);
        setSlug((s) => s || list[0]?.slug || "");
      })
      .catch((err) => setError(errorText(err)));
    api.listModules()
      .then((list) => setOn(list.find((m) => m.id === "contest")?.enabled === true))
      .catch(() => setOn(false));
  }, []);

  const load = useCallback(async (): Promise<void> => {
    if (!slug || on !== true) return;
    try {
      setContests(await contestApi.list(slug));
    } catch (err) {
      setError(errorText(err));
    }
  }, [slug, on]);

  // Solange eines läuft, alle zehn Sekunden nachsehen – dann wächst der Zähler mit.
  const running = contests.some((c) => c.status === "open");
  useEffect(() => {
    void load();
    if (timer.current) window.clearInterval(timer.current);
    timer.current = window.setInterval(() => void load(), running ? 10_000 : 60_000);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [load, running]);

  const loadDetail = useCallback(async (id: string): Promise<void> => {
    setDetail(await contestApi.get(id));
    if (mayRun) {
      const list = await contestApi.entries(id).catch(() => null);
      setEntries(list ? { keep_days: list.keep_days, entries: list.entries } : null);
    }
  }, [mayRun]);

  useEffect(() => {
    if (!open) {
      setDetail(null);
      setEntries(null);
      return;
    }
    void loadDetail(open).catch((err) => setError(errorText(err)));
  }, [open, loadDetail]);

  const act = async (what: () => Promise<unknown>, message: string): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await what();
      setNotice(message);
      await load();
      if (open) await loadDetail(open);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const draw = async (id: string): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const res = await contestApi.draw(id);
      setDrawn(res.drawn[0]?.name ?? null);
      setNotice(res.message);
      await load();
      await loadDetail(id);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  if (on === false) {
    return (
      <ModulAus title="Gewinnspiel" icon="gift">
        „Gewinnspiel“ ist nicht eingeschaltet. Das geht unter Aussehen → Mitmach-Module.
      </ModulAus>
    );
  }

  const zeigeForm = mayRun && (formOffen || contests.length === 0);
  const laufend = contests.filter((c) => c.status === "open").length;
  const teilnahmen = contests.reduce((n, c) => n + c.entries, 0);

  return (
    <Page className="max-w-5xl">
      <PageHeader
        eyebrow="Hörer"
        title="Gewinnspiel"
        description={<>
          Etwas verlosen, Teilnahmen sammeln, den Gewinner ziehen. Der Knopf zum Ziehen gibt es auch im Studio, im Fach „Mitmachen“.
          {!mayRun && " Starten, Ziehen und die Teilnahmeliste sind Moderation und Leitung vorbehalten."}
        </>}
        actions={<>
          <KanalWahl channels={channels} slug={slug} onChange={(v) => { setSlug(v); setOpen(null); }} />
          {mayRun && contests.length > 0 && (
            <button onClick={() => setFormOffen((o) => !o)} className={`btn ${formOffen ? "btn-secondary" : "btn-primary"}`}>
              <Sym name={formOffen ? "x" : "plus"} />{formOffen ? "Formular schließen" : "Neues Gewinnspiel"}
            </button>
          )}
        </>}
      />

      <Meldungen error={error} notice={notice} onClear={() => setNotice(null)} />

      {drawn && (
        <section className="card brand-glow relative mb-5 overflow-hidden p-6 text-center animate-fade-in"
          style={{ boxShadow: "var(--shadow-card), 0 0 0 1.5px rgb(var(--brand-primary-rgb) / 0.55)" }}>
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-brand-primary/15 text-brand-primary">
            <Sym name="party" className="h-6 w-6" />
          </div>
          <div className="section-label">Gezogen</div>
          <div className="mt-1 text-3xl font-semibold tracking-tight text-primary">{drawn}</div>
          <button onClick={() => setDrawn(null)} className="btn btn-ghost btn-sm mt-3">
            ausblenden
          </button>
        </section>
      )}

      {contests.length > 0 && (
        <div className="mb-5 grid grid-cols-3 gap-3">
          {[
            { label: "Gewinnspiele", value: contests.length },
            { label: "laufen gerade", value: laufend, ton: laufend > 0 ? "text-emerald-600 dark:text-emerald-400" : "" },
            { label: "Teilnahmen", value: teilnahmen },
          ].map((k) => (
            <div key={k.label} className="card px-4 py-3">
              <div className={`text-xl font-semibold tabular-nums text-primary ${k.ton ?? ""}`}>{k.value}</div>
              <div className="text-xs text-muted">{k.label}</div>
            </div>
          ))}
        </div>
      )}

      {zeigeForm && (
        <Card title="Neues Gewinnspiel" description="Titel, Preis und wie man mitmacht – der Rest ist freiwillig." className="mb-5 animate-fade-in">
          <ContestForm busy={busy} slug={slug} onCreate={(input) => { setFormOffen(false); void act(() => contestApi.create(slug, input), "Gewinnspiel angelegt."); }} />
        </Card>
      )}

      <div className="section-label mb-2">Gewinnspiele</div>
      {contests.length === 0 && (
        <div className="card p-2">
          <EmptyState icon={<Sym name="gift" className="h-5 w-5" />} title="Noch nichts da."
            description={mayRun ? "Legt oben das erste Gewinnspiel an." : undefined} />
        </div>
      )}
      <ul className="space-y-3">
        {contests.map((c) => (
          <li key={c.id} className={`card overflow-hidden ${c.status === "open" ? "ring-1 ring-emerald-500/30" : ""}`}>
            <div className="p-4 sm:p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex min-w-0 flex-1 items-start gap-3">
                  <span className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ${c.status === "open" ? "bg-brand-primary/10 text-brand-primary" : "bg-input text-muted"}`}
                    style={c.status === "open" ? undefined : { boxShadow: "inset 0 0 0 1px var(--border)" }}>
                    <Sym name="gift" className="h-5 w-5" />
                  </span>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-semibold text-primary">{c.title}</h3>
                      <StandSchild stand={STATUS_STAND[c.status]}>
                        {STATUS_TEXT[c.status]}
                        {c.status === "open" && !c.running && " (außerhalb des Zeitraums)"}
                      </StandSchild>
                    </div>
                    {c.prize && <p className="mt-0.5 text-sm text-secondary">Zu gewinnen: <span className="font-medium text-primary">{c.prize}</span></p>}
                    {c.ends_at && (
                      <p className="mt-1 flex items-center gap-1.5 text-xs text-muted"><Sym name="clock" className="h-3.5 w-3.5" />Einsendeschluss: {whenText(c.ends_at)}</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-4 max-sm:w-full max-sm:border-t max-sm:border-default max-sm:pt-3 sm:text-right">
                  <div>
                    <div className="text-xl font-semibold tabular-nums leading-none text-primary">{c.entries}</div>
                    <div className="mt-1 text-[0.7rem] text-muted">Teilnahmen</div>
                  </div>
                  {c.mode !== "mitmachen" && (
                    <div>
                      <div className="text-xl font-semibold tabular-nums leading-none text-primary">{c.valid_entries}</div>
                      <div className="mt-1 text-[0.7rem] text-muted">gültig</div>
                    </div>
                  )}
                  {c.winners > 0 && (
                    <div>
                      <div className="text-xl font-semibold tabular-nums leading-none text-brand-primary">{c.winners}</div>
                      <div className="mt-1 text-[0.7rem] text-muted">gezogen</div>
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                {c.status === "draft" && (
                  <button
                    disabled={!mayRun || busy}
                    onClick={() => void act(() => contestApi.open(c.id), "Das Gewinnspiel läuft.")}
                    className="btn btn-primary btn-sm"
                  >
                    <Sym name="play" className="h-3 w-3" />Starten
                  </button>
                )}
                {c.status !== "draft" && (
                  <button
                    disabled={!mayRun || busy || c.valid_entries <= c.winners}
                    onClick={() => void draw(c.id)}
                    title={c.valid_entries <= c.winners ? "In der Lostrommel liegt nichts mehr" : "Zieht zufällig aus allen gültigen Teilnahmen"}
                    className="btn btn-primary btn-sm"
                  >
                    <Sym name="sparkles" className="h-3.5 w-3.5" />{c.winners > 0 ? "Noch einen ziehen" : "Gewinner ziehen"}
                  </button>
                )}
                {c.status === "open" && (
                  <button
                    disabled={!mayRun || busy}
                    onClick={() => void act(() => contestApi.close(c.id), "Gewinnspiel beendet.")}
                    className="btn btn-secondary btn-sm"
                  >
                    Beenden
                  </button>
                )}
                <button onClick={() => setOpen(open === c.id ? null : c.id)} className={`btn btn-sm ${open === c.id ? "btn-secondary" : "btn-ghost"}`}>
                  <Sym name={open === c.id ? "up" : "down"} className="h-3.5 w-3.5" />
                  {open === c.id ? "zuklappen" : "Teilnahmen und Ziehungen"}
                </button>
                <span className="ml-auto flex items-center gap-1">
                  {mayRun && (
                    <button
                      onClick={() => void contestApi.downloadEntries(c.id).catch((err) => setError(errorText(err)))}
                      className="btn btn-ghost btn-sm"
                    >
                      <Sym name="download" className="h-3.5 w-3.5" />CSV herunterladen
                    </button>
                  )}
                  {c.status !== "open" && (
                    <button
                      disabled={!mayRun || busy}
                      onClick={() => void act(() => contestApi.remove(c.id), "Gelöscht.")}
                      className="btn btn-ghost btn-sm"
                    >
                      <Sym name="trash" className="h-3.5 w-3.5" />löschen
                    </button>
                  )}
                </span>
              </div>
            </div>

            {open === c.id && detail && (
              <div className="grid gap-5 border-t border-default bg-surface-sunken/60 p-4 sm:p-5 md:grid-cols-[minmax(0,1fr)_16rem]">
                <div className="min-w-0">
                  <h4 className="section-label mb-2">Teilnahmen</h4>
                  {mayRun && entries ? (
                    <EntryList
                      entries={entries.entries}
                      keepDays={entries.keep_days}
                      busy={busy}
                      onMark={(entryId, mark) =>
                        void act(() => contestApi.markWinner(entryId, mark), mark ? "Als Gewinner markiert." : "Markierung zurückgenommen.")
                      }
                    />
                  ) : (
                    <p className="text-sm text-muted">
                      Namen und Kontakte sehen nur Moderation und Leitung. Teilnahmen bisher: {detail.entries}.
                    </p>
                  )}
                </div>
                <div className="min-w-0 space-y-5">
                  {detail.winner_list.length > 0 && (
                    <div>
                      <h4 className="section-label mb-2">Gewinner</h4>
                      <ul className="space-y-2 text-sm">
                        {detail.winner_list.map((w) => (
                          <li key={w.id} className="flex items-center gap-2.5 rounded-lg border border-brand-primary/30 bg-brand-primary/[0.06] px-3 py-2">
                            <Sym name="star" className="h-4 w-4 text-brand-primary" />
                            <span className="min-w-0">
                              <span className="block truncate font-medium text-primary">{w.purged ? <span className="text-muted">(gelöscht)</span> : w.name}</span>
                              {w.contact && <span className="block truncate text-xs text-muted">{w.contact}</span>}
                              <span className="block text-xs text-muted">{whenText(w.won_at)}</span>
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {detail.draws.length > 0 && (
                    <div>
                      <h4 className="section-label mb-1">Ziehungen</h4>
                      <p className="mb-2 text-xs text-muted">Damit später nachvollziehbar ist, wann aus wie vielen Losen gezogen wurde.</p>
                      <ol className="space-y-1.5 border-l-2 border-default pl-3 text-xs text-muted">
                        {detail.draws.map((d) => (
                          <li key={d.id}>
                            <span className="tabular-nums text-secondary">{whenText(d.drawn_at)}</span> · aus {d.pool} {d.pool === 1 ? "Los" : "Losen"}
                            {d.by && ` · ${d.by}`}
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}
                  {detail.winner_list.length === 0 && detail.draws.length === 0 && (
                    <p className="text-xs text-muted">Noch nicht gezogen.</p>
                  )}
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
    </Page>
  );
}
